import React, { useMemo, useCallback, useEffect, useRef } from "react";
import { normalizeColumnOrder } from "../interfaccia/colonneTabella/columnOrder";
import {
  MODA_ARTICOLI_COLUMN_IDS, MODA_ARTICOLI_LOCKED_COLUMN_IDS,
} from "./modaArticoliColumnMetadata";
import { STATO_ARTICOLI_COLUMN_IDS, STATO_ARTICOLI_LOCKED_COLUMN_IDS } from "./statoArticoliColumnMetadata";
import { buildStatoArticoliColumns } from "./statoArticoliColumns";
import { TextSkeleton } from "../interfaccia/loading";
import DataTableBase from "../interfaccia/DataTableBase";
import TagModaCell from "./TagModaCell";
import PrestashopSitesCell, {
  normalizePrestashopNumber, renderPrestashopVisibility,
} from "./PrestashopSitesCell";
import associaImmagineENomeANomeUtente from "../interfaccia/funzioniComuni/associaImmagineENomeANomeUtente";
import stilePulsanti from "../interfaccia/stilePulsanti";

const MODA_CORRECTION_FIELDS = {
  persona: "persona", reparto: "reparto", categoria: "categoria",
  tipoOggetto: "tipo", sottotipo: "sottotipo",
};

const withModaCorrectionCell = (column) => {
  const field = MODA_CORRECTION_FIELDS[column.id];
  if (!field) return column;
  return {
    ...column,
    cell: (row) => {
      const modaValue = column.selector(row);
      const requested = row.moda_correzioni_pending?.[field];
      if (!String(requested ?? "").trim()) return modaValue;
      const displayedModa = String(modaValue ?? "").trim() || "–";
      return (
        <span title={`Moda: ${displayedModa} · richiesto in Domino: ${requested}`}>
          {`M:${displayedModa}, D:${requested}`}
        </span>
      );
    },
  };
};

function ModaArticoliTable({
  data,
  loading,
  totalRows,
  page,
  perPage,
  perPageOptions,
  sortBy,
  sortDirection,
  onChangePage,
  onChangeRowsPerPage,
  onSort,
  currentUserEmail = "",
  noDataComponent,
  onOpenProduct,
  columnPreference,
  mobile = false,
  variante = "pubblicazione",
}) {
  const statoArticoli = variante === "statoArticoli";
  const columnIds = statoArticoli ? STATO_ARTICOLI_COLUMN_IDS : MODA_ARTICOLI_COLUMN_IDS;
  const lockedIds = statoArticoli ? STATO_ARTICOLI_LOCKED_COLUMN_IDS : MODA_ARTICOLI_LOCKED_COLUMN_IDS;
  const { tastoApriAspetto } = stilePulsanti;
  const normalizeBoolean = useCallback((value) => {
    if (typeof value === "boolean") {
      return value;
    }
    if (typeof value === "number") {
      return value !== 0;
    }
    if (typeof value === "string") {
      const normalized = value.trim().toLowerCase();
      if (!normalized.length) {
        return false;
      }
      if (["1", "true", "si", "sì", "yes", "y"].includes(normalized)) {
        return true;
      }
      if (["0", "false", "no", "n"].includes(normalized)) {
        return false;
      }
    }
    return Boolean(value);
  }, []);
  const renderUserAvatar = useCallback(
    (row, keys) => {
      const rawValue =
        keys
          .map((key) => row?.[key])
          .find(
            (value) => typeof value === "string" && value.trim().length > 0,
          ) ?? "";
      if (!rawValue) {
        return <span className="gray f7">-</span>;
      }
      const avatar = associaImmagineENomeANomeUtente(
        rawValue,
        currentUserEmail,
        64,
      );
      const nomeDisplay =
        avatar?.nomeDisplay?.trim() || rawValue?.trim() || "Utente";
      const src = avatar?.pathImmagine;
      return (
        <div className="tc" title={nomeDisplay}>
          <div
            className="center br-100 bg-light-gray dib"
            style={{ width: 36, height: 36 }}
          >
            {src ? (
              <img
                src={src}
                alt={nomeDisplay}
                className="br-100"
                style={{ width: 36, height: 36 }}
              />
            ) : (
              <span className="f6 lh-copy flex items-center justify-center h-100">
                {nomeDisplay.charAt(0).toUpperCase()}
              </span>
            )}
          </div>
        </div>
      );
    },
    [currentUserEmail],
  );

  const baseColumns = useMemo(
    () => [
      {
        id: "codiceVS",
        name: "Cod.VS",
        selector: (row) =>
          row.codiceVS ?? row.codice_vs ?? row.cod_vs ?? row.codicevs ?? "",
        cell: (row) => {
          const codice =
            row.codiceVS ?? row.codice_vs ?? row.cod_vs ?? row.codicevs ?? "";
          const isOnlyPs = Boolean(row.pubbl_solo_su_ps);
          return (
            <button
              type="button"
              className={`pointer bn bg-transparent flex items-center ${
                Number(row.annullato) === 1 ? "dark-red" : ""
              }`}
              onClick={() => {
                if (isOnlyPs) {
                  const conferma = window.confirm(
                    "Attenzione! questo è un articolo vecchio ed è presente solo su prestahop. Se continui verrà creato un documento vuoto in Domino e dovrai inserire tutti i dati. Ti consiglio di recuperare testi e tutto da prestashop.",
                  );
                  if (!conferma) {
                    return;
                  }
                }
                onOpenProduct?.({
                  codVS: codice,
                  row,
                });
              }}
            >
              <strong className="mr2">{codice}</strong>
              <span>{tastoApriAspetto}</span>
            </button>
          );
        },
        sortable: false,
        /* sortField: "codiceVS", */
        minWidth: "100px",
        compact: true,
      },
      {
        id: "importatoDa",
        name: "Import",
        cell: (row) => {
          const username = String(row.ut_import_done ?? row.utImportDone ?? "").trim();
          if (!username) return <span className="gray f7">-</span>;
          const date = row.timeImportDoneItaly || row.time_import_done || "Data non registrata";
          return <span title={`Ultimo import: ${username} · ${date}`} aria-label={`Ultimo import: ${username} · ${date}`}>
            {renderUserAvatar(row, ["ut_import_done", "utImportDone"])}
          </span>;
        },
        sortable: true,
        sortField: "utImportDone",
        width: "65px",
        center: true,
        compact: true,
      },
      {
        id: "testiPubblicatiDa",
        name: "P.Testi",
        cell: (row) => {
          const rawValue =
            ["ut_pubbl_done", "utPubblDone", "pubblicatiDa"]
              .map((key) => row?.[key])
              .find(
                (value) => typeof value === "string" && value.trim().length > 0,
              ) ?? "";
          const isPublished =
            row.pubblicato &&
            (row.pubblicato ?? "").toString().toUpperCase() !== "NO";

          // Se l'utente che ha pubblicato è presente, mostra l'avatar
          if (rawValue) {
            return renderUserAvatar(row, [
              "ut_pubbl_done",
              "utPubblDone",
              "pubblicatiDa",
            ]);
          }

          // Se non c'è l'utente ma l'articolo è pubblicato (pubblicato !== "NO"), mostra un cerchio con punto interrogativo
          if (isPublished) {
            return (
              <div className="tc">
                <div
                  className="center br-100 sfondoColScuro dib flex items-center justify-center"
                  style={{ width: 36, height: 36 }}
                >
                  <span className="f5 b">?</span>
                </div>
              </div>
            );
          }

          // Altrimenti mostra il trattino
          return <span className="gray f7">-</span>;
        },
        sortable: false,
        width: "60px",
        center: true,
        compact: true,
      },
      {
        id: "immaginiPubblicateDa",
        name: "P.Imm",
        cell: (row) => {
          const rawValue =
            ["ut_imm_done", "utImmDone", "immaginiDa"]
              .map((key) => row?.[key])
              .find(
                (value) => typeof value === "string" && value.trim().length > 0,
              ) ?? "";
          const hasImages = row.ha_immagine ?? false;

          // Se l'utente che ha caricato le immagini è presente, mostra l'avatar
          if (rawValue) {
            return renderUserAvatar(row, [
              "ut_imm_done",
              "utImmDone",
              "immaginiDa",
            ]);
          }

          // Se non c'è l'utente ma le immagini esistono, mostra un cerchio con punto interrogativo
          if (hasImages) {
            return (
              <div className="tc">
                <div
                  className="center br-100 sfondoColScuro dib flex items-center justify-center"
                  style={{ width: 36, height: 36 }}
                >
                  <span className="f5 b">?</span>
                </div>
              </div>
            );
          }

          // Altrimenti mostra il trattino
          return <span className="gray f7">-</span>;
        },
        sortable: false,
        width: "60px",
        center: true,
        compact: true,
      },
      {
        id: "testiAssegnatiA",
        name: "A.Testi",
        cell: (row) =>
          renderUserAvatar(row, [
            "ut_pubbl_pren",
            "utPubblPren",
            "assegnatoTesti",
          ]),
        sortable: false,
        width: "60px",
        center: true,
        compact: true,
      },
      {
        id: "immaginiAssegnateA",
        name: "A.Imm",
        cell: (row) =>
          renderUserAvatar(row, [
            "ut_imm_pren",
            "utImmPren",
            "assegnatoImmagini",
          ]),
        sortable: false,
        width: "60px",
        center: true,
        compact: true,
      },

      {
        id: "nome",
        name: "Nome",
        selector: (row) =>
          row.nome ??
          row.descrizione ??
          row.descrizioneBreve ??
          row.descrizione_breve ??
          "",
        sortable: true,
        sortField: "nome",
        minWidth: "250px",
        grow: 2,
        wrap: true,
      },
      {
        id: "colore",
        name: "Colore",
        selector: (row) =>
          row.colore ?? row.nomeColore ?? row.nome_colore ?? "",
        sortable: true,
        sortField: "colore",
        minWidth: "140px",
        wrap: true,
      },
      {
        id: "brand",
        name: "Brand",
        selector: (row) => row.brand ?? row.brand_nome ?? row.brandName ?? "",
        sortable: true,
        sortField: "brand",
        minWidth: "80px",
        compact: true,
      },
      {
        id: "stockWeb",
        name: "StockW",
        selector: (row) => row.stock ?? "",
        cell: (row) => <PrestashopSitesCell row={row} field="stock"
          legacyValue={row.stock ?? ""} normalizeValue={normalizePrestashopNumber} />,
        sortable: true,
        sortField: "stock",
        width: "100px",
        compact: true,
      },
      {
        id: "stockModa",
        name: "StockM",
        selector: (row) => row.stockModa ?? row.stock_moda ?? "",
        sortable: true,
        sortField: "stockModa",
        width: "110px",
        compact: true,
      },
      {
        id: "persona",
        name: "Pers.",
        selector: (row) =>
          row.persona ?? row.target_persona ?? row.target ?? "",
        sortable: true,
        sortField: "persona",
        width: "50px",
        style: { minWidth: "50px", maxWidth: "50px" },
        headerStyle: {
          minWidth: "50px",
          maxWidth: "50px",
          whiteSpace: "normal",
        },
        wrap: true,
        compact: true,
      },
      {
        // Stagione d'acquisto (Moda [tab stagione]).
        id: "stagioneAcquisto",
        name: "acq",
        selector: (row) =>
          row.stagione ?? row.season ?? row.stagione_corrente ?? "",
        sortable: true,
        sortField: "stagione",
        width: "50px",
        style: { minWidth: "50px", maxWidth: "50px" },
        headerStyle: {
          minWidth: "50px",
          maxWidth: "50px",
          whiteSpace: "normal",
        },
        wrap: true,
        compact: true,
      },
      {
        // Stagione di lancio (Moda [tab gruppo9], MODA_Articoli.stagione_lancio).
        id: "stagioneLancio",
        name: "lancio",
        selector: (row) => row.stagioneLancio ?? row.stagione_lancio ?? "",
        sortable: true,
        sortField: "stagioneLancio",
        width: "50px",
        style: { minWidth: "50px", maxWidth: "50px" },
        headerStyle: {
          minWidth: "50px",
          maxWidth: "50px",
          whiteSpace: "normal",
        },
        wrap: true,
        compact: true,
      },
      {
        id: "tagModa",
        name: "Tag",
        cell: (row) => <TagModaCell tags={row.tagModa} />,
        width: "56px",
        style: { minWidth: "56px", maxWidth: "56px" },
        compact: true,
      },
      {
        id: "pubblicato",
        name: "Pubblicato",
        selector: (row) => row.pubblicato ?? "",
        sortable: true,
        sortField: "pubblicato",
        minWidth: "110px",
        wrap: true,
      },
      {
        id: "codiceProduttore",
        name: "Codice Produttore",
        selector: (row) =>
          row.codiceProduttore ??
          row.codice_produttore ??
          row.codiceProd ??
          row.cod_prod ??
          "",
        sortable: true,
        sortField: "codProd",
        minWidth: "160px",
        wrap: true,
      },
      {
        id: "codiceModello",
        name: "Codice Modello",
        selector: (row) =>
          row.codiceModello ??
          row.codice_modello ??
          row.codModello ??
          row.codMod ??
          row.cod_mod ??
          "",
        sortable: true,
        sortField: "codiceModello",
        minWidth: "160px",
        wrap: true,
      },
      {
        id: "codiceColore",
        name: "Codice Colore",
        selector: (row) =>
          row.codiceColore ??
          row.codice_colore ??
          row.codColore ??
          row.codCol ??
          row.cod_col ??
          "",
        sortable: true,
        sortField: "codiceColore",
        minWidth: "100px",
        wrap: true,
        compact: true,
      },
      {
        id: "reparto",
        name: "Rep.",
        selector: (row) =>
          row.reparto ??
          row.repartoDescrizione ??
          row.reparto_descrizione ??
          "",
        sortable: true,
        sortField: "reparto",
        width: "70px",
        style: { minWidth: "70px", maxWidth: "70px" },
        headerStyle: {
          minWidth: "70px",
          maxWidth: "70px",
          whiteSpace: "normal",
        },
        wrap: true,
        compact: true,
      },
      {
        id: "categoria",
        name: "Cat.",
        selector: (row) =>
          row.categoria ??
          row.categoria_path ??
          row.categoriaPath ??
          row.percorso_categoria ??
          "",
        sortable: true,
        sortField: "categoria",
        width: "70px",
        style: { minWidth: "70px", maxWidth: "70px" },
        headerStyle: {
          minWidth: "70px",
          maxWidth: "70px",
          whiteSpace: "normal",
        },
        wrap: true,
        compact: true,
      },
      {
        id: "tipoOggetto",
        name: "Tipo",
        selector: (row) =>
          row.tipoOggetto ??
          row.tipo_oggetto ??
          row.tipo ??
          row.tipologia ??
          "",
        sortable: true,
        sortField: "tipoOggetto",
        width: "70px",
        style: { minWidth: "70px", maxWidth: "70px" },
        headerStyle: {
          minWidth: "70px",
          maxWidth: "70px",
          whiteSpace: "normal",
        },
        wrap: true,
        compact: true,
      },
      {
        id: "sottotipo",
        name: "Sottotipo",
        selector: (row) =>
          row.sottotipo ?? row.sotto_tipo ?? row.subtipo ?? row.subtype ?? "",
        sortable: true,
        sortField: "sottotipo",
        width: "70px",
        style: { minWidth: "70px", maxWidth: "70px" },
        headerStyle: {
          minWidth: "70px",
          maxWidth: "70px",
          whiteSpace: "normal",
        },
        wrap: true,
        compact: true,
      },
      {
        id: "idPrestashop",
        name: "idPS",
        selector: (row) => row.id_ps ?? row.idPs ?? "",
        sortable: true,
        sortField: "id_ps",
        width: "60px",
        compact: true,
      },
      {
        id: "testiImportati",
        name: "Testi importati",
        selector: (row) =>
          row.testi_importati ?? row.testiImportati ?? row.testiimportati ?? 0,
        cell: (row) => {
          const value =
            row.testi_importati ??
            row.testiImportati ??
            row.testiimportati ??
            0;
          const isImported =
            typeof value === "string"
              ? value.trim() !== "" && value !== "0"
              : Number(value) === 1;
          return (
            <span className={isImported ? "dark-green" : "gray"}>
              {isImported ? "Si" : "No"}
            </span>
          );
        },
        sortable: false,
        minWidth: "120px",
        center: true,
        compact: true,
      },
      {
        id: "categoriaPrincipale",
        name: "Cat. principale",
        selector: (row) => row.cat_principale ?? row.catPrincipale ?? "",
        cell: (row) => <PrestashopSitesCell row={row} field="catDefault"
          legacyValue={row.cat_principale ?? row.catPrincipale ?? ""} />,
        sortable: true,
        sortField: "cat_principale",
        minWidth: "160px",
        wrap: true,
      },
      {
        id: "attivoPrestashop",
        name: "Attivo PS",
        selector: (row) =>
          normalizeBoolean(
            row.attivo ?? row.attivo_ps ?? row.attivoPS ?? row.ps_attivo,
          ),
        cell: (row) => {
          const attivo = normalizeBoolean(
            row.attivo ?? row.attivo_ps ?? row.attivoPS ?? row.ps_attivo,
          );
          const renderActive = (value) => (
            <span className={normalizeBoolean(value) ? "dark-green" : "dark-red"}>
              {normalizeBoolean(value) ? "✔" : "✘"}
            </span>
          );
          return <PrestashopSitesCell row={row} field="attivo"
            legacyValue={renderActive(attivo)} normalizeValue={normalizeBoolean}
            renderValue={renderActive} />;
        },
        sortable: false,
        minWidth: "80px",
        center: true,
        compact: true,
      },
      {
        id: "visibilita",
        name: "Vis.",
        selector: (row) => {
          const value = (row.visibility ?? "").toString().toLowerCase();
          if (value === "both") {
            return "Si";
          }
          if (value === "none") {
            return "No";
          }
          return row.visibility ?? "";
        },
        sortable: true,
        cell: (row) => <PrestashopSitesCell row={row} field="visibility"
          legacyValue={String(row.visibility ?? "").toLowerCase() === "both" ? "Si"
            : String(row.visibility ?? "").toLowerCase() === "none" ? "No" : row.visibility ?? ""}
          renderValue={renderPrestashopVisibility} />,
        sortField: "visibility",
        minWidth: "40px",
        wrap: true,
        compact: true,
      },
      {
        id: "motivoDisattivazione",
        name: "Motivo",
        selector: (row) =>
          row.deactivation_reason ?? row.deactivationReason ?? "",
        cell: (row) => <PrestashopSitesCell row={row} field="deactivationReason"
          legacyValue={row.deactivation_reason ?? row.deactivationReason ?? ""} />,
        sortable: false,
        minWidth: "160px",
        wrap: true,
      },
      {
        id: "immaginiPresenti",
        name: "Imm.",
        selector: (row) => {
          const hasImages =
            row.hasImmagini ?? row.has_immagini ?? row.ha_immagine ?? false;
          return hasImages ? "Si" : "No";
        },
        cell: (row) => {
          const hasImages =
            row.hasImmagini ?? row.has_immagini ?? row.ha_immagine ?? false;
          return (
            <span className={hasImages ? "dark-green" : "gray"}>
              {hasImages ? "Si" : "No"}
            </span>
          );
        },
        sortable: false,
        minWidth: "40px",
        center: true,
        compact: true,
      },
      {
        id: "immagineNonTrovata",
        name: "I. non si trova",
        selector: (row) => {
          const value =
            row.imm_nonsitrova ??
            row.immNonSiTrova ??
            row.imageNotFound ??
            false;
          return value ? "Si" : "No";
        },
        sortable: true,
        sortField: "imm_nonsitrova",
        minWidth: "70px",
        wrap: true,
        compact: true,
      },
      {
        id: "ultimaModifica",
        name: "Ultima mod.",
        selector: (row) => {
          const rawDate =
            row.sourceUpdatedAt ?? row.source_updated_at ?? row.sourceupdateat;
          if (!rawDate) {
            return "";
          }
          const date = new Date(rawDate);
          if (Number.isNaN(date.getTime())) {
            return rawDate;
          }
          return date.toLocaleString("it-IT");
        },
        sortable: true,
        sortField: "source_updated_at",
        minWidth: "170px",
      },
      {
        id: "vendite48Ore",
        name: "48h Vendite",
        cell: (row) => <PrestashopSitesCell row={row} field="ultimeVendite48h"
          legacyValue={row["48OreUltimaVendita"] ?? row["48oreultimavendita"] ?? ""}
          normalizeValue={normalizePrestashopNumber} />,
        selector: (row) =>
          row["48OreUltimaVendita"] ??
          row["48oreultimavendita"] ??
          row["48OreUltimaVendita".toLowerCase()] ??
          "",
        sortable: false,
        width: "120px",
        right: true,
      },
    ].map(withModaCorrectionCell),
    [renderUserAvatar, normalizeBoolean, onOpenProduct, tastoApriAspetto],
  );

  const defaultColumns = useMemo(() => statoArticoli ? buildStatoArticoliColumns(baseColumns) : baseColumns,
    [statoArticoli, baseColumns]);

  const customizable = Boolean(columnPreference) && !mobile;
  const draggingRef = useRef(false);
  const suppressClickRef = useRef(false);
  const dragEndTimerRef = useRef(null);
  useEffect(() => () => clearTimeout(dragEndTimerRef.current), []);

  const columns = useMemo(() => normalizeColumnOrder(
    customizable ? columnPreference.order : [], columnIds, lockedIds,
  ).map((id) => ({
    ...defaultColumns.find((column) => column.id === id),
    omit: customizable && columnPreference.columns[id] === false && !lockedIds.includes(id),
    reorder: customizable && columnPreference.ready && !lockedIds.includes(id),
  })), [defaultColumns, customizable, columnPreference, columnIds, lockedIds]);

  // Anche i target con reorder:false ricevono dragEnter dalla libreria.
  const guardColumnDrag = (event) => {
    const header = event.target.closest?.(".rdt_TableCol");
    if (!columnPreference.ready || !header?.closest(".rdt_TableHeadRow") ||
        !columnIds.includes(header.dataset.columnId) ||
        lockedIds.includes(header.dataset.columnId)) {
      event.preventDefault();
      event.stopPropagation();
      return;
    }
    if (event.type === "dragstart") draggingRef.current = true;
  };
  const endColumnDrag = () => {
    if (!draggingRef.current) return;
    draggingRef.current = false;
    suppressClickRef.current = true;
    clearTimeout(dragEndTimerRef.current);
    dragEndTimerRef.current = setTimeout(() => { suppressClickRef.current = false; }, 0);
  };

  return (
    <div className="sfondoBiancoTema br3"
      onDragStartCapture={customizable ? guardColumnDrag : undefined}
      onDragEnterCapture={customizable ? guardColumnDrag : undefined}
      onDragEndCapture={customizable ? endColumnDrag : undefined}
      onClickCapture={(event) => {
        if (suppressClickRef.current && event.target.closest?.(".rdt_TableHeadRow")) {
          event.preventDefault();
          event.stopPropagation();
        }
      }}>
      <DataTableBase
        {...(statoArticoli ? { progressComponent: <div className="pa3 w-100" role="status" aria-label="Caricamento articoli"><TextSkeleton lines={10} lineHeight={28} /></div> } : {})}
        columns={columns}
        onColumnOrderChange={customizable && columnPreference.ready
          ? (nextColumns) => columnPreference.reorder(normalizeColumnOrder(
            nextColumns.map((column) => column.id), columnIds, lockedIds,
          )) : undefined}
        data={data}
        progressPending={loading}
        pagination
        paginationServer
        paginationTotalRows={totalRows}
        paginationPerPage={perPage}
        paginationRowsPerPageOptions={perPageOptions}
        paginationDefaultPage={page}
        onChangeRowsPerPage={onChangeRowsPerPage}
        onChangePage={onChangePage}
        sortServer
        onSort={(column, direction) => {
          if (!draggingRef.current && !suppressClickRef.current) onSort(column.sortField ?? column.name, direction);
        }}
        defaultSortFieldId={defaultColumns.find((column) => (column.sortField ?? column.name) === sortBy)?.id}
        defaultSortAsc={sortDirection !== "desc"}
        highlightOnHover
        dense
        noDataComponent={
          noDataComponent ?? "Nessun articolo trovato per i filtri correnti."
        }
        keyField="id"
      />
    </div>
  );
}

export default ModaArticoliTable;
